import ReactModal from 'react-modal';
import General from '../utils/GeneralUtils';
import UniversalTag from './UniversalTag';
import './EmailVerificationSlideout.scss';
import './EmailFormatSlideout.scss';

const EMAIL_FORMAT_TOKENS = [
  {
    token: '{first}',
    description: 'Full first name',
    example: 'jane',
  },
  {
    token: '{last}',
    description: 'Full last name',
    example: 'doe',
  },
  {
    token: '{f}',
    description: 'First initial',
    example: 'j',
  },
  {
    token: '{l}',
    description: 'Last initial',
    example: 'd',
  },
  {
    token: '{m}',
    description: 'Middle initial',
    example: 'm',
  },
  {
    token: '{nickname}',
    description: 'Nickname equivalent of first name',
    example: '"jenny" for Jane',
  },
  {
    token: '{n}',
    description: 'Nickname initial',
    example: 'j',
  },
  {
    token: '{number}',
    description: 'Numeric suffix',
    example: '123',
  },
];

const EMAIL_FORMAT_EXAMPLES = [
  {
    pattern: '{first}.{last}',
    description: 'First name, dot, last name',
    example: 'jane.doe@school.edu',
  },
  {
    pattern: '{f}{last}',
    description: 'First initial followed by last name',
    example: 'jdoe@school.edu',
  },
  {
    pattern: '{first}_{last}',
    description: 'First name, underscore, last name',
    example: 'jane_doe@school.edu',
  },
  {
    pattern: '{last}.{first}',
    description: 'Last name, dot, first name',
    example: 'doe.jane@school.edu',
  },
  {
    pattern: '{f}{m}{last}',
    description: 'First initial, middle initial, last name',
    example: 'jmdoe@school.edu',
  },
  {
    pattern: '{first}.{m}.{last}',
    description: 'First name, middle initial, last name with dots',
    example: 'jane.m.doe@school.edu',
  },
  {
    pattern: '{f}{last}{number}',
    description: 'First initial, last name, with numeric suffix',
    example: 'jdoe2@school.edu',
  },
  {
    pattern: '{nickname}.{last}',
    description: 'Nickname, dot, last name',
    example: 'jenny.doe@school.edu (for Jane Doe)',
  },
];

interface EmailFormatSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EmailFormatSlideout({ isOpen, onClose }: EmailFormatSlideoutProps) {
  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      style={General.getSliderDefaults({ width: 'min(90vw, 500px)' })}
      parentSelector={General.getModalRoot}
    >
      <div className="email-verification-slideout">
        <div className="header">
          <h3>Email Format Patterns</h3>
        </div>

        <div className="section intro">
          <p>Email format patterns describe how a contact's email address is constructed from their name. This helps predict email addresses for other contacts at the same organization.</p>
        </div>

        <div className="section">
          <h4>Format Tokens</h4>
          <table>
            <tbody>
              {EMAIL_FORMAT_TOKENS.map((tokenInfo) => (
                <tr key={tokenInfo.token} className="email-format-slideout__row">
                  <td className="status-cell email-format-slideout__tag-cell">
                    <UniversalTag category={tokenInfo.token} />
                  </td>
                  <td className="desc">
                    {tokenInfo.description}
                    <span className="email-format-slideout__example">e.g., "{tokenInfo.example}"</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="section">
          <h4>Common Patterns</h4>
          <table>
            <tbody>
              {EMAIL_FORMAT_EXAMPLES.map((formatInfo) => (
                <tr key={formatInfo.pattern} className="email-format-slideout__row">
                  <td className="status-cell email-format-slideout__tag-cell">
                    <UniversalTag category={formatInfo.pattern} />
                  </td>
                  <td className="desc">
                    {formatInfo.description}
                    <span className="email-format-slideout__example">{formatInfo.example}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </ReactModal>
  );
}
